import type { ReactNode } from "react";

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-xs font-medium uppercase tracking-wider text-neutral-600">
          {label}
        </span>
        {hint && <span className="text-[11px] text-neutral-400">{hint}</span>}
      </div>
      {children}
    </label>
  );
}

export function TextInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full rounded border px-3 py-2 text-sm focus:border-neutral-900 focus:outline-none"
    />
  );
}

export function NumberInput({
  value,
  onChange,
  step = 0.01,
}: {
  value: number;
  onChange: (v: number) => void;
  step?: number;
}) {
  return (
    <input
      type="number"
      step={step}
      value={value}
      onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
      className="w-full rounded border px-3 py-2 text-sm focus:border-neutral-900 focus:outline-none"
    />
  );
}

export function TextArea({
  value,
  onChange,
  rows = 4,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  rows?: number;
  placeholder?: string;
}) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows={rows}
      placeholder={placeholder}
      className="w-full rounded border px-3 py-2 text-sm focus:border-neutral-900 focus:outline-none resize-y"
    />
  );
}

export function Toggle({
  value,
  onChange,
  label,
}: {
  value: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <label className="inline-flex items-center gap-2 text-sm">
      <input
        type="checkbox"
        checked={value}
        onChange={(e) => onChange(e.target.checked)}
        className="size-4 rounded border-neutral-400"
      />
      <span>{label}</span>
    </label>
  );
}

export function BulletList({
  value,
  onChange,
  rows = 4,
  hint = "One per line",
}: {
  value: string[];
  onChange: (v: string[]) => void;
  rows?: number;
  hint?: string;
}) {
  return (
    <>
      <textarea
        value={value.join("\n")}
        onChange={(e) =>
          onChange(
            e.target.value.split("\n").map((s) => s.trim()).filter((s, i, arr) =>
              s !== "" || i < arr.length - 1
            )
          )
        }
        rows={rows}
        className="w-full rounded border px-3 py-2 text-sm focus:border-neutral-900 focus:outline-none resize-y font-mono"
      />
      <div className="mt-1 text-[11px] text-neutral-400">{hint}</div>
    </>
  );
}
